import { Button, Grid, makeStyles, Snackbar, Typography } from '@material-ui/core';
import { User, UserRole } from '../../../domain/domain';
import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { ApplicationState } from '../../../reducers';
import { compose } from "redux";
import { connect } from "react-redux";
import { ConfirmationDialog } from '../../../components/Dialogs/ConfirmationDialog';
import { exportCrsToXML } from '../../../api/title';
import { Alert } from "../../Alert/Alert";
import { useErrorNotification } from '../../../contexts/ErrorNotificationContext';
import { SNACKBAR_LONG_AUTO_HIDE_DURATION_MS } from '../../../api/common';

const useStyles = makeStyles(_ => ({
    root: {
        marginTop: 100
    },
    nav: {
        marginTop: 50
    },
    button: {
        textTransform: 'none',
        color: '#186ea8',
        fontSize: 24,
        fontWeight: 300,
        '&:hover': {
            backgroundColor: 'inherit',
            color: '#2e9e9e'
        }
    }
}));


type Props = {
    currentUser: User
}

const AdministratorPage: React.FC<Props> = ({ currentUser }) => {
    const classes = useStyles();
    const history = useHistory();
    const { showError } = useErrorNotification();
    const [exportCrsConfirmationDialogOpen, setExportCrsConfirmationDialogOpen] = useState<boolean>(false);
    const [snackBarOpen, setSnackBarOpen] = useState<boolean>(false);
    const [snackBarMessage, setSnackBarMessage] = useState<string>("");
    const [snackBarSeverity, setSnackBarSeverity] = useState<"success" | "error">("success");
    
    const exportCrs = () => {
        setExportCrsConfirmationDialogOpen(true);
    }

    useEffect(() => {
        if (currentUser.userRole !== UserRole.ADMINISTRATOR) {
            showError(
                'Access Denied',
                'You do not have permission to access this page.',
                'Only administrators can access this page.'
            );
        }
    }, [currentUser.userRole, showError]);

    if (currentUser.userRole !== UserRole.ADMINISTRATOR) {
        return (
            <div style={{ textAlign: "center" }} className="m-xsmall-top-2">
                <div className="m-xsmall-top-1">Access Denied. You do not have permission to access this page.</div>
            </div>
        );
    }

    return (
        <>
            <Snackbar open={snackBarOpen} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} autoHideDuration={SNACKBAR_LONG_AUTO_HIDE_DURATION_MS} onClose={() => { setSnackBarOpen(false) }}>
                <Alert onClose={() => { setSnackBarOpen(false) }} severity={snackBarSeverity}>
                    {snackBarMessage}
                </Alert>
            </Snackbar>
            <ConfirmationDialog
                open={exportCrsConfirmationDialogOpen}
                title="Export CRS to XML"
                contentText="Are you sure you want to export all CRS titles to XML; this is a long running process?"
                handleNo={() => {
                    setExportCrsConfirmationDialogOpen(false);
                }}
                handleYes={() => {
                    exportCrsToXML();
                    setExportCrsConfirmationDialogOpen(false);
                    setSnackBarSeverity("success");
                    setSnackBarMessage("The export is in progress. You will receive an email when the export is complete.");
                    setSnackBarOpen(true);
                }}
            />
            <Grid container direction='column' alignContent='center' className={`${classes.root} flex-gap-5`} alignItems='center'>
                <Grid item>
                    <Typography variant='h1'>Administrator Page</Typography>
                </Grid>
                <Grid item className={classes.nav}>
                    <Grid container direction='column' alignItems='center'>
                        <Grid item>
                            <Button size="large" className={classes.button} onClick={() => {
                                history.push("/crs")
                            }}>Last Published Date</Button>
                        </Grid>
                        {currentUser.userRole === UserRole.ADMINISTRATOR &&
                            <Grid item>
                                <Button size="large" className={classes.button} onClick={() => {
                                    history.push("/admin/users")
                                }}>User Management</Button>
                            </Grid>
                        }
                        {currentUser.userRole === UserRole.ADMINISTRATOR &&
                            <Grid item>
                                <Button size="large" className={classes.button} onClick={() => {
                                    history.push("/marked-for-deletion")
                                }}>Content Marked For Deletion</Button>
                            </Grid>
                        }
                        {currentUser.userRole === UserRole.ADMINISTRATOR &&
                            <Grid item>
                                <Button size="large" className={classes.button} onClick={() => {
                                    history.push("/editorial-templates")
                                }}>Edit Editorial Templates</Button>
                            </Grid>
                        }
                        {currentUser.userRole === UserRole.ADMINISTRATOR &&
                            <Grid item>
                                <Button size="large" className={classes.button} onClick={() => { exportCrs() }}>Export CRS to XML</Button>
                            </Grid>
                        }
                    </Grid>
                </Grid>
            </Grid>
        </>
    )
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        currentUser: state.user.currentUser
    };
};

const mapDispatchToProps = (dispatch: any) => {
    return { dispatch };
}

export default compose<any>(
    connect(mapStateToProps, mapDispatchToProps)
)(AdministratorPage);